import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "../../../../static/img/SearchIcon.png";
import {
	DebtActionButtons,
	DebtDetailsLink,
	DebtFilterButton,
	DebtFilterSection,
	DebtHeaderActionButton,
	DebtTableEmptyPanel,
	StationRequestFilterToolbar,
	DebtListHeader,
	DebtListSection,
	DebtListTitle,
	DebtSearchIcon,
	DebtSearchInput,
	DebtSearchInputWrapper,
	DebtSelect,
	DebtStatusBadge,
	DebtTable,
	DebtTableCell,
	DebtTableCheckbox,
	DebtTableHeader,
	DebtTableRow,
	BulkActionButtons,
	BulkActionButton,
	DebtPayActionButton,
	DebtApproveActionButton,
	DebtRejectActionButton,
} from "../styled/DebtManagementStyles";
import { PaginationWrapperTop } from "../../MyBooking/styled/DesStyled";
import NumberPaginationBox from "../../../controls/components/numberPaginationBox/NumberPaginationBox";
import useCommonListFunctions from "../../../hooks/useCommonList/useCommonListFunctions";
import { TranslateLabels, useLanguage } from "../../../controls/layout/content/MultiLanguge";
import {
	AdminPartnerPaymentRequestRow,
	GroupedStationRequestRow,
	GroupedStationRequestDetailsResponse,
	fetchGroupedPartnerPaymentRequests,
	fetchGroupedPartnerPaymentRequestDetails,
	adminBulkPayGroupedPartnerRequests,
	adminBulkSendFinalizedEmail,
	adminApproveGroupedPartnerRequests,
	adminCreateWiseTransfer,
	adminGetWiseTransferStatus,
	adminRefreshWiseTransfer,
	adminRejectGroupedPartnerRequests,
	WiseTransferInfo, adminGetWiseReturned, type WiseReturnedInfo } from "../../../../services/PartnerPaymentRequestService";
import RejectReasonModal from "./RejectReasonModal";
import { getPartnerDetail } from "../../../../services/PartnerService";
import Partner from "../../../../models/Partner";
import Location from "../../../../models/Location";
import { getListDestinations } from "../../../../services/LocationService";
import { getCountryOptions } from "../../../../services/CountryRatesService";
import { normalizeCountryKey, toCanonicalCountryName } from "../../CountryRates/countryCatalog";
import PaymentConfirmModal from "./PaymentConfirmModal";
import { useIsSuperAdmin } from "../../../hooks/useIsSuperAdmin";
import PermissionGate from "../../../controls/auth/PermissionGate";
import PaymentHistoryModal from "./PaymentHistoryModal";
import CycleBookingsBreakdownModal from "./CycleBookingsBreakdownModal";
import { formatPartnerPayoutMoney } from "./PartnerPayoutDetailsContent";

function mapCyclePayloadToAdminRequests(data: GroupedStationRequestDetailsResponse): AdminPartnerPaymentRequestRow[] {
	const cc = data.currencyCode ?? null;
	const pid = data.partnerId;
	return (data.items || []).map((it) => ({
		id: it.id,
		partnerId: it.partnerId,
		station: {
			id: pid,
			name: null,
			displayName: null,
			destinationId: null,
			cityName: null,
			countryName: null,
			currencyCode: it.pricingCurrencyCode ?? cc,
		},
		requestTime: it.requestTime,
		requestAmount: it.requestAmount,
		requestType: it.requestType ?? undefined,
		status: it.status,
		paidAmount: it.paidAmount,
		paidTime: it.paidTime,
		orderCount: it.orderCount,
		orders: it.orders,
	}));
}

/** Lý do Pay tay tối thiểu — khớp PAY_TAY_LY_DO_MIN ở backend (payTayGuard). */
const MANUAL_PAY_REASON_MIN = 10;
/** 12 columns: checkbox, partner id, name, cycle, month, requests, request type, amount, bookings, status, pay, details */
/* Cột Action rộng đủ cho 2 nút (Approve+Reject / Pay+Wise) không đè sang Details; các cột ít chữ
   (ID, Requests, Month) thu lại cho vừa khung ~1180px mà không phải cuộn ngang (Quân 05/10/2026). */
const GROUP_GRID =
	"32px minmax(64px,0.45fr) minmax(104px,1.05fr) minmax(76px,0.55fr) minmax(72px,0.5fr) minmax(56px,0.35fr) minmax(96px,0.7fr) minmax(120px,0.85fr) minmax(76px,0.5fr) minmax(96px,0.6fr) minmax(180px,1.1fr) 60px";
const GROUP_GAP = "10px";
/* Pay tay và nút Wise cùng một bề ngang — xếp dọc thẳng hàng cho gọn mắt. */
const ACTION_BTN_WIDTH = 136;

const PAGE_SIZE_MIN = 1;
const PAGE_SIZE_MAX = 100;
const PRESET_PAGE_SIZES = [25, 50, 100] as const;

function groupKey(r: GroupedStationRequestRow) {
	return `${r.partnerId}-${r.vnYear}-${r.vnMonth}-${r.period}`;
}

function aggregateTypeLabel(t: string) {
	if (t === "UNDER_MIN_PAYOUT") return "Below min payout";
	return "Above min payout";
}

function cycleNameFromPeriod(period: 1 | 2): string {
	return period === 1 ? "First Cycle" : "Second Cycle";
}

function monthLabelFromVn(vnYear: number, vnMonth: number): string {
	const idx = vnMonth - 1;
	const monthShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][idx] || "";
	return `${monthShort} ${vnYear}`;
}

/** Match partner financial UI default when upstream omits currency (Vietnam-first ops). */
const FALLBACK_DEBT_DISPLAY_CURRENCY = "VND";

function normalizeDebtCurrency(code: string | null | undefined): string | null {
	const s = `${code ?? ""}`.trim();
	return s ? s.toUpperCase() : null;
}

/**
 * Grouped list row + flat admin payment-request feed + optional API fields.
 * Order: grouped payload → station on row → any linked request’s station snapshot → VND fallback.
 */
function resolveGroupCurrencyCode(
	r: GroupedStationRequestRow,
	currencyByRequestId: Map<number, string | null>
): string {
	const direct = normalizeDebtCurrency(r.currencyCode) ?? normalizeDebtCurrency(r.station?.currencyCode);
	if (direct) return direct;
	for (const id of r.requestIds || []) {
		const cc = currencyByRequestId.get(id);
		if (cc) return cc;
	}
	return FALLBACK_DEBT_DISPLAY_CURRENCY;
}

/** Sum `requestAmount` for every ID in the group using the flat admin feed (canonical per-request amounts). */
function sumRequestAmountsForGroup(group: GroupedStationRequestRow, requestAmountById: Map<number, number>): number {
	const ids = group.requestIds || [];
	if (ids.length === 0) return Number(group.totalAmount) || 0;
	let sum = 0;
	let matched = 0;
	for (const id of ids) {
		if (requestAmountById.has(id)) {
			sum += requestAmountById.get(id) ?? 0;
			matched++;
		}
	}
	if (matched === ids.length) return sum;
	return Number(group.totalAmount) || sum;
}

/** Native `<input type="month">` shows ugly dash placeholders when empty — use labeled select instead. */
function buildRollingMonthSelectOptions(lang: string) {
	const allLabel = lang === "vi" ? "Tất cả tháng" : "All months";
	const opts: { value: string; label: string }[] = [{ value: "", label: allLabel }];
	const now = new Date();
	const locale = lang === "vi" ? "vi-VN" : "en-GB";
	for (let i = 0; i < 48; i++) {
		const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
		const y = d.getFullYear();
		const m = d.getMonth() + 1;
		const value = `${y}-${String(m).padStart(2, "0")}`;
		let label: string;
		if (lang === "vi") {
			label = `Tháng ${m}/${y}`;
		} else {
			label = d.toLocaleString(locale, { month: "short", year: "numeric" });
		}
		opts.push({ value, label });
	}
	return opts;
}

export default function StationRequestListPanel(props: {
	onPaidSuccess: () => Promise<void>;
	/** Same feed as legacy Payment history modal — filters by Partner when Details expands */
	partnerPaymentRequests: AdminPartnerPaymentRequestRow[];
}) {
	const { page, doChangePage } = useCommonListFunctions();
	const { lang } = useLanguage();

	const [searchInput, setSearchInput] = useState("");
	const [appliedSearch, setAppliedSearch] = useState("");
	const [selectedStatus, setSelectedStatus] = useState("all");
	const [appliedStatus, setAppliedStatus] = useState("all");
	const [selectedPeriod, setSelectedPeriod] = useState<"all" | "1" | "2">("all");
	const [appliedPeriod, setAppliedPeriod] = useState<"all" | "1" | "2">("all");
	const [monthValue, setMonthValue] = useState("");
	const [appliedMonth, setAppliedMonth] = useState("");
	const [selectedCountry, setSelectedCountry] = useState("all");
	const [selectedRegion, setSelectedRegion] = useState("all");
	const [appliedCountry, setAppliedCountry] = useState("all");
	const [appliedRegion, setAppliedRegion] = useState("all");
	const [countryOptions, setCountryOptions] = useState<string[]>([]);
	const [destinations, setDestinations] = useState<Location[]>([]);

	const [rows, setRows] = useState<GroupedStationRequestRow[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(false);
	const [payoutDetailsRow, setPayoutDetailsRow] = useState<GroupedStationRequestRow | null>(null);
	const [cycleDetailPayload, setCycleDetailPayload] = useState<GroupedStationRequestDetailsResponse | null>(null);
	const [cycleDetailLoading, setCycleDetailLoading] = useState(false);
	const [cycleDetailError, setCycleDetailError] = useState<string | null>(null);
	const [paymentDetailsPartnerBank, setPaymentDetailsPartnerBank] = useState<Partner | null>(null);
	/** Row selected for “Chi tiết” booking breakdown (separate from payout details modal). */
	const [bookingBreakdownRow, setBookingBreakdownRow] = useState<GroupedStationRequestRow | null>(null);
	const [bookingBreakdownPayload, setBookingBreakdownPayload] = useState<GroupedStationRequestDetailsResponse | null>(
		null
	);
	const [bookingBreakdownLoading, setBookingBreakdownLoading] = useState(false);
	const [bookingBreakdownError, setBookingBreakdownError] = useState<string | null>(null);
	/** groupKey → request IDs at time of selection (needed so bulk email works across pages) */
	const [selectedByGroupKey, setSelectedByGroupKey] = useState<Record<string, number[]>>({});

	const [pageSize, setPageSize] = useState(25);
	const [pageSizeInput, setPageSizeInput] = useState("25");

	const [payGroup, setPayGroup] = useState<GroupedStationRequestRow | null>(null);
	const [payAmountInput, setPayAmountInput] = useState("");
	const [payPartnerDetail, setPayPartnerDetail] = useState<Partner | null>(null);
	const [paySubmitting, setPaySubmitting] = useState(false);
	const [payNote, setPayNote] = useState("");
	/* 03/11/2026 (Quân): Pay tay chỉ super admin — người khác chỉ thấy Pay qua Wise. */
	const { isSuperAdmin } = useIsSuperAdmin();

	const [bulkSending, setBulkSending] = useState(false);

	const [approveGroup, setApproveGroup] = useState<GroupedStationRequestRow | null>(null);
	const [approveSubmitting, setApproveSubmitting] = useState(false);
	const [rejectGroup, setRejectGroup] = useState<GroupedStationRequestRow | null>(null);
	const [rejectSubmitting, setRejectSubmitting] = useState(false);

	const canonicalCountryOptions = useMemo(() => {
		const byKey = new Map<string, string>();

		[...countryOptions, ...destinations.map((destination) => String(destination?.country || "").trim())].forEach(
			(countryRaw) => {
				const trimmed = String(countryRaw || "").trim();
				if (!trimmed) return;
				const key = normalizeCountryKey(trimmed);
				if (!key || byKey.has(key)) return;
				byKey.set(key, toCanonicalCountryName(trimmed) || trimmed);
			}
		);

		return Array.from(byKey.values()).sort((a, b) => a.localeCompare(b));
	}, [countryOptions, destinations]);

	const loadGrouped = useCallback(async () => {
		setLoading(true);
		try {
			let vnYear: number | undefined;
			let vnMonth: number | undefined;
			if (appliedMonth && /^(\d{4})-(\d{2})$/.test(appliedMonth)) {
				vnYear = parseInt(appliedMonth.slice(0, 4), 10);
				vnMonth = parseInt(appliedMonth.slice(5, 7), 10);
			}

			const statusAggregate = appliedStatus === "30" ? "IN_TRANSIT" : appliedStatus === "40" ? "PAID" : "all";

			const destinationId =
				appliedRegion !== "all" && `${appliedRegion}`.trim() !== "" ? String(appliedRegion) : undefined;

			const data = await fetchGroupedPartnerPaymentRequests({
				page: page || 1,
				pageSize,
				search: appliedSearch || undefined,
				statusAggregate: statusAggregate as "IN_TRANSIT" | "PAID" | "all",
				period: appliedPeriod === "all" ? "all" : (Number(appliedPeriod) as 1 | 2),
				vnYear,
				vnMonth,
				destinationId,
			});
			setRows(data.items || []);
			setTotal(data.total || 0);
		} catch (e) {
			console.error(e);
			setRows([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	}, [page, pageSize, appliedSearch, appliedStatus, appliedPeriod, appliedMonth, appliedRegion]);

	const monthSelectOptions = useMemo(() => buildRollingMonthSelectOptions(lang), [lang]);

	const requestAmountById = useMemo(() => {
		const m = new Map<number, number>();
		for (const row of props.partnerPaymentRequests || []) {
			m.set(row.id, Number(row.requestAmount) || 0);
		}
		return m;
	}, [props.partnerPaymentRequests]);

	const currencyByRequestId = useMemo(() => {
		const m = new Map<number, string | null>();
		for (const row of props.partnerPaymentRequests || []) {
			m.set(row.id, normalizeDebtCurrency(row.station?.currencyCode));
		}
		return m;
	}, [props.partnerPaymentRequests]);

	const filteredDestinations = useMemo(() => {
		const normalizedCountry = selectedCountry === "all" ? "" : normalizeCountryKey(selectedCountry || "");
		return destinations
			.filter((destination) => {
				if (!destination?.id) return false;
				if (!normalizedCountry) return true;
				return normalizeCountryKey(destination.country || "") === normalizedCountry;
			})
			.sort((a, b) => `${a.name || ""}`.localeCompare(`${b.name || ""}`));
	}, [destinations, selectedCountry]);

	useEffect(() => {
		let cancelled = false;
		void (async () => {
			try {
				const [{ data }, countries] = await Promise.all([getListDestinations(1, 500), getCountryOptions()]);
				if (!cancelled) {
					setDestinations(data || []);
					setCountryOptions(countries || []);
				}
			} catch {
				if (!cancelled) {
					setDestinations([]);
					setCountryOptions([]);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (selectedRegion === "all") return;
		const exists = filteredDestinations.some((d) => `${d.id || ""}` === `${selectedRegion}`);
		if (!exists) setSelectedRegion("all");
	}, [filteredDestinations, selectedRegion]);

	useEffect(() => {
		void loadGrouped();
	}, [loadGrouped]);

	useEffect(() => {
		if (!payGroup) {
			setPayPartnerDetail(null);
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const d = await getPartnerDetail(String(payGroup.partnerId));
				if (!cancelled) setPayPartnerDetail(d);
			} catch {
				if (!cancelled) setPayPartnerDetail(null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [payGroup]);

	const cycleDetailPartnerId = payoutDetailsRow?.partnerId ?? null;

	useEffect(() => {
		if (cycleDetailPartnerId == null) {
			setPaymentDetailsPartnerBank(null);
			return;
		}
		let cancelled = false;
		void (async () => {
			try {
				const detail = await getPartnerDetail(String(cycleDetailPartnerId));
				if (!cancelled) setPaymentDetailsPartnerBank(detail);
			} catch {
				if (!cancelled) setPaymentDetailsPartnerBank(null);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [cycleDetailPartnerId]);

	useEffect(() => {
		const row = payoutDetailsRow;
		if (!row) {
			setCycleDetailPayload(null);
			setCycleDetailError(null);
			setCycleDetailLoading(false);
			return;
		}
		let cancelled = false;
		setCycleDetailLoading(true);
		setCycleDetailError(null);
		setCycleDetailPayload(null);
		void fetchGroupedPartnerPaymentRequestDetails({
			partnerId: row.partnerId,
			vnYear: row.vnYear,
			vnMonth: row.vnMonth,
			period: row.period,
		})
			.then((data) => {
				if (!cancelled) setCycleDetailPayload(data);
			})
			.catch((e: any) => {
				if (!cancelled)
					setCycleDetailError(e?.response?.data?.message || e?.message || "Failed to load cycle details");
			})
			.finally(() => {
				if (!cancelled) setCycleDetailLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [payoutDetailsRow?.partnerId, payoutDetailsRow?.vnYear, payoutDetailsRow?.vnMonth, payoutDetailsRow?.period]);

	useEffect(() => {
		const row = bookingBreakdownRow;
		if (!row) {
			setBookingBreakdownPayload(null);
			setBookingBreakdownError(null);
			setBookingBreakdownLoading(false);
			return;
		}
		let cancelled = false;
		setBookingBreakdownLoading(true);
		setBookingBreakdownError(null);
		setBookingBreakdownPayload(null);
		void fetchGroupedPartnerPaymentRequestDetails({
			partnerId: row.partnerId,
			vnYear: row.vnYear,
			vnMonth: row.vnMonth,
			period: row.period,
		})
			.then((data) => {
				if (!cancelled) setBookingBreakdownPayload(data);
			})
			.catch((e: any) => {
				if (!cancelled)
					setBookingBreakdownError(
						e?.response?.data?.message || e?.message || "Failed to load cycle bookings"
					);
			})
			.finally(() => {
				if (!cancelled) setBookingBreakdownLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [
		bookingBreakdownRow?.partnerId,
		bookingBreakdownRow?.vnYear,
		bookingBreakdownRow?.vnMonth,
		bookingBreakdownRow?.period,
	]);

	const payoutModalRequests = useMemo(() => {
		if (!cycleDetailPayload) return [];
		return mapCyclePayloadToAdminRequests(cycleDetailPayload);
	}, [cycleDetailPayload]);

	const selectedRequestIdsForEmail = useMemo(() => {
		const ids = Object.values(selectedByGroupKey).flat();
		return Array.from(new Set(ids));
	}, [selectedByGroupKey]);

	const selectedGroupCount = Object.keys(selectedByGroupKey).length;

	const clampPageSize = useCallback((n: number) => {
		return Math.min(PAGE_SIZE_MAX, Math.max(PAGE_SIZE_MIN, n));
	}, []);

	const commitPageSizeFromInput = useCallback(() => {
		const parsed = parseInt(pageSizeInput.trim(), 10);
		const next = clampPageSize(Number.isFinite(parsed) ? parsed : pageSize);
		setPageSize(next);
		setPageSizeInput(String(next));
		doChangePage?.(1);
	}, [pageSizeInput, pageSize, clampPageSize, doChangePage]);

	const applyPresetPageSize = useCallback(
		(n: number) => {
			const next = clampPageSize(n);
			setPageSize(next);
			setPageSizeInput(String(next));
			doChangePage?.(1);
		},
		[clampPageSize, doChangePage]
	);

	const toggleSelectGroup = (r: GroupedStationRequestRow) => {
		const k = groupKey(r);
		setSelectedByGroupKey((prev) => {
			const next = { ...prev };
			if (Object.prototype.hasOwnProperty.call(next, k)) delete next[k];
			else next[k] = [...(r.requestIds || [])];
			return next;
		});
	};

	const openPaymentDetailsPopup = (r: GroupedStationRequestRow) => {
		setPayoutDetailsRow(r);
	};

	const closePayoutDetailsModal = () => {
		setPayoutDetailsRow(null);
	};

	const openBookingBreakdown = (r: GroupedStationRequestRow) => {
		if ((Number(r.totalBookings) || 0) <= 0) return;
		setBookingBreakdownRow(r);
	};

	const closeBookingBreakdown = () => {
		setBookingBreakdownRow(null);
	};

	const handleFilter = () => {
		setAppliedSearch(searchInput.trim());
		setAppliedStatus(selectedStatus);
		setAppliedPeriod(selectedPeriod);
		setAppliedMonth(monthValue);
		setAppliedCountry(selectedCountry);
		setAppliedRegion(selectedRegion);
		setSelectedByGroupKey({});
		doChangePage?.(1);
	};

	const openPay = (r: GroupedStationRequestRow) => {
		setPayGroup(r);
		setPayAmountInput(String(Number(r.pendingAmount).toFixed(2)));
		setPayNote("");
	};

	const closePay = () => {
		if (paySubmitting) return;
		setPayGroup(null);
		setPayAmountInput("");
		setPayNote("");
	};

	const confirmPay = async () => {
		if (!payGroup) return;
		const amt = parseFloat(`${payAmountInput}`.replace(/,/g, ""));
		if (!Number.isFinite(amt) || amt <= 0) {
			alert("Enter a valid payment amount.");
			return;
		}
		if (payNote.trim().length < MANUAL_PAY_REASON_MIN) {
			alert(`Enter a reason (at least ${MANUAL_PAY_REASON_MIN} characters) for paying outside Wise.`);
			return;
		}
		setPaySubmitting(true);
		try {
			await adminBulkPayGroupedPartnerRequests({
				partnerId: payGroup.partnerId,
				vnYear: payGroup.vnYear,
				vnMonth: payGroup.vnMonth,
				period: payGroup.period,
				paidAmount: amt,
				note: payNote.trim(),
			});
			closePay();
			await props.onPaidSuccess();
			await loadGrouped();
			setSelectedByGroupKey({});
			alert("Bulk payment completed for this cycle group.");
		} catch (e: any) {
			alert(e?.response?.data?.message || e?.message || "Payment failed");
		} finally {
			setPaySubmitting(false);
		}
	};

	const handleBulkEmail = async () => {
		const ids = selectedRequestIdsForEmail;
		if (ids.length === 0) return;
		if (
			!window.confirm(
				`Send finalized payout email (template 2) for ${ids.length} underlying request(s) from selected groups?`
			)
		) {
			return;
		}
		setBulkSending(true);
		try {
			await adminBulkSendFinalizedEmail(ids);
			setSelectedByGroupKey({});
			alert("Emails queued / sent.");
		} catch (e: any) {
			alert(e?.response?.data?.message || e?.message || "Bulk email failed");
		} finally {
			setBulkSending(false);
		}
	};

	const [wiseSubmittingKey, setWiseSubmittingKey] = useState<string | null>(null);
	/* Trạng thái lệnh Wise mới nhất của từng request (Quân 05/10/2026): nút "Pay via Wise" phải
	   cho biết lệnh đã tạo / đang chờ duyệt / đã chi / bị huỷ, thay vì đứng nguyên như chưa bấm. */
	const [wiseByRequestId, setWiseByRequestId] = useState<Record<number, WiseTransferInfo | null>>({});
	const [wiseErrorByRequestId, setWiseErrorByRequestId] = useState<Record<number, string>>({});
	const [wiseRefreshingId, setWiseRefreshingId] = useState<number | null>(null);

	const applyWiseTransfers = useCallback((requestId: number, transfers: WiseTransferInfo[] | undefined) => {
		setWiseByRequestId((prev) => ({ ...prev, [requestId]: (transfers && transfers[0]) || null }));
	}, []);

	const loadWiseStatuses = useCallback(
		async (requestIds: number[]) => {
			await Promise.all(
				requestIds.map(async (id) => {
					try {
						const out = await adminGetWiseTransferStatus(id);
						applyWiseTransfers(id, out?.transfers);
					} catch {
						/* lỗi đọc trạng thái không chặn bảng — nút giữ trạng thái cũ */
					}
				})
			);
		},
		[applyWiseTransfers]
	);

	/* Request đang IN_TRANSIT và đứng một mình (mới Pay via Wise được) → đọc trạng thái lệnh Wise. */
	const wiseEligibleIds = useMemo(
		() =>
			rows
				.filter((r) => String(r.status || "").toUpperCase() === "IN_TRANSIT" && (r.requestIds || []).length === 1)
				.map((r) => Number((r.requestIds || [])[0]))
				.filter((id) => Number.isFinite(id) && id > 0),
		[rows]
	);

	useEffect(() => {
		if (wiseEligibleIds.length) loadWiseStatuses(wiseEligibleIds);
	}, [wiseEligibleIds, loadWiseStatuses]);

	/* Còn lệnh đang chờ duyệt / đang chuyển → 30 giây đọc lại một lần (webhook Wise cập nhật DB gần như tức thì).
	   Có lệnh vừa lên PAID thì nạp lại bảng để dòng đó chuyển sang Paid. */
	useEffect(() => {
		const openIds = wiseEligibleIds.filter((id) => ["AWAITING_APPROVE", "SENT"].includes(wiseByRequestId[id]?.status || ""));
		if (!openIds.length) return;
		const timer = window.setInterval(async () => {
			await loadWiseStatuses(openIds);
		}, 30000);
		return () => window.clearInterval(timer);
	}, [wiseEligibleIds, wiseByRequestId, loadWiseStatuses]);

	/* 17/10/2026 (Quân): lệnh Wise đã chi nhưng ngân hàng trả lại ⇒ bật lại nút chi cho Admin. Cổng trạm KHÔNG đổi
	   (yêu cầu vẫn Paid) — dữ liệu lấy từ Wise qua backend (wise-returned), không đoán. */
	const [returnedByRequestId, setReturnedByRequestId] = useState<Record<number, WiseReturnedInfo>>({});
	const loadReturned = useCallback(async () => {
		try {
			const out = await adminGetWiseReturned();
			const map: Record<number, WiseReturnedInfo> = {};
			for (const x of out?.returned || []) map[Number(x.paymentRequestId)] = x;
			setReturnedByRequestId(map);
		} catch {
			/* lỗi đọc không chặn bảng */
		}
	}, []);
	useEffect(() => {
		loadReturned();
	}, [rows, loadReturned]);
	const returnedIdOf = (r: GroupedStationRequestRow) =>
		(r.requestIds || []).map(Number).find((id) => returnedByRequestId[id]) || null;

	const confirmResendViaWise = async (r: GroupedStationRequestRow, reqId: number) => {
		const info = returnedByRequestId[reqId];
		if (
			!window.confirm(
				`The station's bank RETURNED Wise transfer #${info?.wiseTransferId} for request #${reqId} (${r.station?.name || "station"}) — the money is back in Wise and the station has NOT been paid.\n\n` +
					"Check and fix the station's bank account first, otherwise it will bounce again.\n\n" +
					"Create a new Wise transfer for the same amount? It waits for approval in the Wise app. The station portal does not change."
			)
		) {
			return;
		}
		setWiseSubmittingKey(groupKey(r));
		try {
			let out: any;
			try {
				out = await adminCreateWiseTransfer(reqId, { resend: true });
			} catch (e: any) {
				const data = e?.response?.data;
				if (e?.response?.status === 409 && data?.code === "ACCOUNT_MISMATCH") {
					const release = window.confirm(
						`ACCOUNT NUMBER CHECK FAILED\n\n${data.message}\n\n` +
							"Click OK only if you have verified the correct account with the station (Checked — release).\n" +
							"Click Cancel to stop. No transfer has been created."
					);
					if (!release) return;
					out = await adminCreateWiseTransfer(reqId, { resend: true, confirmMismatch: true });
				} else {
					throw e;
				}
			}
			const t = out?.transfer;
			alert(
				t
					? `Wise transfer re-sent: ${t.sourceAmount} USD → ${Number(t.targetAmount || 0).toLocaleString("en-US")} ${t.targetCurrency}.\nApprove it in the Wise app to release the money.`
					: out?.message || "Wise transfer created."
			);
			await loadReturned();
		} catch (e: any) {
			alert(e?.response?.data?.message || e?.message || "Could not re-send the Wise transfer");
		} finally {
			setWiseSubmittingKey(null);
		}
	};

	const anyWisePaid = wiseEligibleIds.some((id) => wiseByRequestId[id]?.status === "PAID");
	useEffect(() => {
		if (anyWisePaid) loadGrouped();
	}, [anyWisePaid]); // eslint-disable-line react-hooks/exhaustive-deps

	const refreshWiseStatus = async (requestId: number) => {
		setWiseRefreshingId(requestId);
		try {
			const out = await adminRefreshWiseTransfer(requestId);
			applyWiseTransfers(requestId, out?.transfers);
		} catch (e: any) {
			alert(e?.response?.data?.message || e?.message || "Could not refresh the Wise transfer status");
		} finally {
			setWiseRefreshingId(null);
		}
	};

	/** Pay qua Wise — chỉ nhóm có ĐÚNG 1 request (mỗi lệnh Wise ứng 1 request); nhóm gộp nhiều request vẫn Pay tay. */
	const confirmPayViaWise_forRow = async (r: GroupedStationRequestRow) => {
		const ids = r.requestIds || [];
		if (ids.length !== 1) {
			alert("Pay via Wise currently supports single-request groups only. Use manual Pay for merged groups.");
			return;
		}
		if (
			!window.confirm(
				`Create a Wise transfer for request #${ids[0]} (${r.station?.name || "station"})?\n` +
					"No money moves yet — the transfer waits for approval in the Wise app. " +
					"Once Wise sends the money, this request flips to PAID automatically."
			)
		) {
			return;
		}
		setWiseSubmittingKey(groupKey(r));
		setWiseErrorByRequestId((prev) => {
			const next = { ...prev };
			delete next[ids[0]];
			return next;
		});
		try {
			let out: any;
			try {
				out = await adminCreateWiseTransfer(ids[0]);
			} catch (e: any) {
				const data = e?.response?.data;
				/* Lớp gác số tài khoản (Quân tick câu 4, 11/09): hồ sơ điểm ≠ người nhận Wise → máy chặn,
				   chỉ đi khi admin xác nhận đã đối chiếu với điểm ("Đã kiểm, cho đi"). */
				if (e?.response?.status === 409 && data?.code === "ACCOUNT_MISMATCH") {
					const release = window.confirm(
						`ACCOUNT NUMBER CHECK FAILED\n\n${data.message}\n\n` +
							"Click OK only if you have verified the correct account with the station (Checked — release).\n" +
							"Click Cancel to stop. No transfer has been created."
					);
					if (!release) return;
					out = await adminCreateWiseTransfer(ids[0], { confirmMismatch: true });
				} else {
					throw e;
				}
			}
			const t = out?.transfer;
			if (t) applyWiseTransfers(ids[0], [t as WiseTransferInfo]);
			alert(
				t
					? `Wise transfer created: send ${t.sourceAmount} USD → ${Number(t.targetAmount || 0).toLocaleString("en-US")} ${t.targetCurrency}.\nApprove it in the Wise app to release the money.`
					: out?.message || "Wise transfer created."
			);
			await loadGrouped();
		} catch (e: any) {
			const msg = e?.response?.data?.message || e?.message || "Wise transfer failed";
			setWiseErrorByRequestId((prev) => ({ ...prev, [ids[0]]: msg }));
			alert(msg);
		} finally {
			setWiseSubmittingKey(null);
		}
	};

	const confirmApprove_forRow = async (r: GroupedStationRequestRow) => {
		const ids = r.requestIds || [];
		if (ids.length === 0) {
			alert("No request IDs found for this group.");
			return;
		}
		if (
			!window.confirm(
				`Approve ${ids.length} request(s) for ${
					r.station?.name || "this station"
				}? They will move to In Transit.`
			)
		) {
			return;
		}
		setApproveGroup(r);
		setApproveSubmitting(true);
		try {
			await adminApproveGroupedPartnerRequests(ids);
			setApproveGroup(null);
			await props.onPaidSuccess();
			await loadGrouped();
			setSelectedByGroupKey({});
		} catch (e: any) {
			// 20/10/2026: backend trả lý do từng lệnh trong errors[] (trạm đang khoá rút tiền, dispute…) — hiện ra.
			const chiTiet = Array.isArray(e?.response?.data?.errors)
				? e.response.data.errors.map((x: any) => `#${x.id}: ${x.reason}`).join("\n")
				: "";
			alert([e?.response?.data?.message || e?.message || "Approve failed", chiTiet].filter(Boolean).join("\n\n"));
		} finally {
			setApproveSubmitting(false);
		}
	};

	const openReject = (r: GroupedStationRequestRow) => {
		setRejectGroup(r);
	};

	const closeReject = () => {
		if (rejectSubmitting) return;
		setRejectGroup(null);
	};

	const confirmReject = async (reason: string) => {
		if (!rejectGroup) return;
		const ids = rejectGroup.requestIds || [];
		if (ids.length === 0) {
			alert("No request IDs found for this group.");
			return;
		}
		setRejectSubmitting(true);
		try {
			await adminRejectGroupedPartnerRequests(ids, reason);
			setRejectGroup(null);
			await props.onPaidSuccess();
			await loadGrouped();
			setSelectedByGroupKey({});
		} catch (e: any) {
			alert(e?.response?.data?.message || e?.message || "Reject failed");
		} finally {
			setRejectSubmitting(false);
		}
	};

	const payCity =
		`${payPartnerDetail?.destination?.name || payPartnerDetail?.destination?.displayName || ""}`.trim() || null;
	const payCountry = `${payPartnerDetail?.country || payPartnerDetail?.destination?.country || ""}`.trim() || null;

	const pageKeys = rows.map(groupKey);
	const allSelectedOnPage =
		pageKeys.length > 0 && pageKeys.every((k) => Object.prototype.hasOwnProperty.call(selectedByGroupKey, k));

	const toggleSelectAllPage = () => {
		setSelectedByGroupKey((prev) => {
			const next = { ...prev };
			if (allSelectedOnPage) {
				pageKeys.forEach((k) => {
					delete next[k];
				});
			} else {
				rows.forEach((r) => {
					next[groupKey(r)] = [...(r.requestIds || [])];
				});
			}
			return next;
		});
	};

	return (
		<>
			<DebtListHeader>
				<DebtListTitle>Station Request List</DebtListTitle>
				<DebtActionButtons>
					<DebtHeaderActionButton
						backgroundColor="#FFFFFF"
						textColor="#005E9E"
						as={Link}
						to="/debt-history"
						style={{ textDecoration: "none" }}>
						<img
							src="./static/img/debt/clock.png"
							alt="Clock"
							style={{ width: "16px", height: "16px", marginRight: "8px" }}
						/>
						{"History lookup"}
					</DebtHeaderActionButton>
					{/* Lớp ② bộ 3 lớp Quân duyệt 11/09: điểm khai tài khoản ngân hàng, admin duyệt ở đây
					    thì hệ mới tạo người nhận trên Wise và mở lại quyền rút tiền. */}
					<DebtHeaderActionButton
						backgroundColor="#FFFFFF"
						textColor="#005E9E"
						as={Link}
						to="/bank-change-review"
						style={{ textDecoration: "none" }}>
						{"Bank account review"}
					</DebtHeaderActionButton>
				</DebtActionButtons>
			</DebtListHeader>

			<DebtListSection>
				<DebtFilterSection>
					<StationRequestFilterToolbar>
						<DebtSearchInputWrapper>
							<DebtSearchIcon
								src={SearchIcon}
								alt="Search"
							/>
							<DebtSearchInput
								type="text"
								placeholder={TranslateLabels[lang]?.DEBT_SEARCH_PLACEHOLDER || "Tìm Partner, Station"}
								value={searchInput}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
								onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
									if (e.key === "Enter") handleFilter();
								}}
							/>
						</DebtSearchInputWrapper>
						<DebtSelect
							value={monthValue}
							onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setMonthValue(e.target.value)}
							style={{ minWidth: 196, maxWidth: 240, flex: "0 1 210px" }}
							aria-label={lang === "vi" ? "Lọc theo tháng (VN)" : "Filter by calendar month"}>
							{monthSelectOptions.map((o) => (
								<option
									key={o.value || "all-months"}
									value={o.value}>
									{o.label}
								</option>
							))}
						</DebtSelect>
						<DebtSelect
							value={selectedCountry}
							onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedCountry(e.target.value)}>
							<option value="all">All countries</option>
							{canonicalCountryOptions.map((country) => (
								<option
									key={country}
									value={country}>
									{country}
								</option>
							))}
						</DebtSelect>
						<DebtSelect
							value={selectedRegion}
							onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedRegion(e.target.value)}>
							<option value="all">All cities</option>
							{filteredDestinations
								.filter((d) => d.id)
								.map((destination, index) => (
									<option
										key={index}
										value={destination.id || ""}>
										{destination.name || ""}
									</option>
								))}
						</DebtSelect>
						<DebtSelect
							value={selectedPeriod}
							onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
								setSelectedPeriod(e.target.value as "all" | "1" | "2")
							}>
							<option value="all">All cycles</option>
							<option value="1">First cycle</option>
							<option value="2">Second cycle</option>
						</DebtSelect>
						<DebtSelect
							value={selectedStatus}
							onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedStatus(e.target.value)}>
							<option value="all">{TranslateLabels[lang]?.DEBT_ALL_STATUS || "All status"}</option>
							<option value="30">In Transit</option>
							<option value="40">Paid</option>
						</DebtSelect>
						<DebtFilterButton
							type="button"
							onClick={handleFilter}>
							{TranslateLabels[lang]?.DEBT_FILTER || "Lọc"}
						</DebtFilterButton>
					</StationRequestFilterToolbar>
				</DebtFilterSection>

				<BulkActionButtons
					style={{
						opacity: selectedRequestIdsForEmail.length > 0 ? 1 : 0,
						transform: "translateY(5px)",
						pointerEvents: selectedRequestIdsForEmail.length > 0 ? "auto" : "none",
					}}>
					<PermissionGate permission="settlements.manage">
						<BulkActionButton
							variant="approve"
							onClick={handleBulkEmail}
							disabled={bulkSending}>
							{bulkSending
								? "Sending..."
								: lang === "vi"
								? `Gửi email (${selectedGroupCount} nhóm · ${selectedRequestIdsForEmail.length} request)`
								: `Bulk email (${selectedGroupCount} groups · ${selectedRequestIdsForEmail.length} requests)`}
						</BulkActionButton>
					</PermissionGate>
				</BulkActionButtons>

				<DebtTable style={{ overflowX: loading || rows.length === 0 ? "visible" : "auto" }}>
					<DebtTableHeader
						style={{
							gridTemplateColumns: GROUP_GRID,
							columnGap: GROUP_GAP,
							textTransform: "none",
							fontSize: "13px",
							fontWeight: 600,
							color: "#475569",
						}}>
						<DebtTableCell>
							<DebtTableCheckbox checked={allSelectedOnPage && pageKeys.length > 0}>
								<input
									type="checkbox"
									checked={allSelectedOnPage && pageKeys.length > 0}
									onChange={toggleSelectAllPage}
								/>
							</DebtTableCheckbox>
						</DebtTableCell>
						<DebtTableCell>Partner ID</DebtTableCell>
						<DebtTableCell>Name</DebtTableCell>
						<DebtTableCell>Cycle</DebtTableCell>
						<DebtTableCell>Month</DebtTableCell>
						<DebtTableCell>Requests</DebtTableCell>
						<DebtTableCell>Request type</DebtTableCell>
						<DebtTableCell alignRight>Amount</DebtTableCell>
						<DebtTableCell alignRight>Bookings</DebtTableCell>
						<DebtTableCell>Status</DebtTableCell>
						<DebtTableCell>Action</DebtTableCell>
						<DebtTableCell>Details</DebtTableCell>
					</DebtTableHeader>

					{loading ? (
						<DebtTableEmptyPanel>
							<strong>{"Loading..."}</strong>
							{"Loading grouped station requests."}
						</DebtTableEmptyPanel>
					) : rows.length === 0 ? (
						<DebtTableEmptyPanel>
							<strong>No data</strong>
							No groups match your filters. Clear the month or status, choose &ldquo;All status&rdquo;,
							then apply filters again.
						</DebtTableEmptyPanel>
					) : (
						rows.map((r) => {
							const k = groupKey(r);
							const checked = Object.prototype.hasOwnProperty.call(selectedByGroupKey, k);
							const st = String(r.status || "").toUpperCase();
							const isRequested = st === "PENDING" || st === "REQUESTED";
							const isInTransit = st === "IN_TRANSIT";
							const isPaid = st === "PAID";
							const isRejected = st === "REJECTED";

							let statusBg: string;
							let statusText: string;
							let statusLabel: string;
							if (isRequested) {
								statusBg = "#EFF6FF";
								statusText = "#1D4ED8";
								statusLabel = "Requested";
							} else if (isInTransit) {
								statusBg = "#FEF3C7";
								statusText = "#B45309";
								statusLabel = "In Transit";
							} else if (isPaid) {
								statusBg = "#ECFDF3";
								statusText = "#067647";
								statusLabel = "Paid";
							} else if (isRejected) {
								statusBg = "#FEF2F2";
								statusText = "#B91C1C";
								statusLabel = "Rejected";
							} else {
								statusBg = "#F3F4F6";
								statusText = "#6B7280";
								statusLabel = st || "Unknown";
							}

							return (
								<DebtTableRow
									key={k}
									backgroundColor="#FFFFFF"
									style={{ gridTemplateColumns: GROUP_GRID, columnGap: GROUP_GAP }}>
									<DebtTableCell>
										<DebtTableCheckbox checked={checked}>
											<input
												type="checkbox"
												checked={checked}
												onChange={() => toggleSelectGroup(r)}
											/>
										</DebtTableCheckbox>
									</DebtTableCell>
									<DebtTableCell>{r.partnerId}</DebtTableCell>
									<DebtTableCell style={{ wordBreak: "break-word" }}>
										{r.station?.name || "—"}
									</DebtTableCell>
									<DebtTableCell>{cycleNameFromPeriod(r.period)}</DebtTableCell>
									<DebtTableCell>{monthLabelFromVn(r.vnYear, r.vnMonth)}</DebtTableCell>
									<DebtTableCell>{r.requestCount}</DebtTableCell>
									<DebtTableCell>{aggregateTypeLabel(String(r.typeAggregate))}</DebtTableCell>
									<DebtTableCell alignRight style={{ whiteSpace: "nowrap" }}>
										{formatPartnerPayoutMoney(
											sumRequestAmountsForGroup(r, requestAmountById),
											resolveGroupCurrencyCode(r, currencyByRequestId)
										)}
									</DebtTableCell>
									<DebtTableCell alignRight>
										{Number(r.totalBookings) > 0 ? (
											<DebtDetailsLink
												to="#"
												onClick={(e) => {
													e.preventDefault();
													openBookingBreakdown(r);
												}}>
												{Number(r.totalBookings)} {lang === "vi" ? "Chi tiết" : "Details"}
											</DebtDetailsLink>
										) : (
											<span style={{ color: "#94a3b8", fontSize: "13px" }}>—</span>
										)}
									</DebtTableCell>
									<DebtTableCell>
										<DebtStatusBadge
										backgroundColor={statusBg}
										textColor={statusText}>
										{statusLabel}
									</DebtStatusBadge>
								</DebtTableCell>
								<DebtTableCell>
									{isRequested ? (
										<PermissionGate permission="settlements.manage">
											<div
												style={{
													display: "flex",
													gap: "8px",
													flexWrap: "nowrap",
													alignItems: "center",
												}}>
												<DebtApproveActionButton
													onClick={() => confirmApprove_forRow(r)}
													disabled={loading || approveSubmitting}>
													Approve
												</DebtApproveActionButton>
												<DebtRejectActionButton
													onClick={() => openReject(r)}
													disabled={loading || rejectSubmitting}>
													Reject
												</DebtRejectActionButton>
											</div>
										</PermissionGate>
									) : isInTransit && r.pendingRequestCount > 0 ? (
										<PermissionGate permission="settlements.manage">
											{/* Xếp dọc: Pay tay ở trên, khối Wise ở dưới — cột Action hẹp, để ngang là đè sang cột Details. */}
											<div
												style={{
													display: "flex",
													flexDirection: "column",
													gap: "6px",
													alignItems: "flex-start",
												}}>
												{(() => {
													const reqId = (r.requestIds || []).length === 1 ? Number(r.requestIds[0]) : null;
													const wt = reqId ? wiseByRequestId[reqId] : null;
													const wStatus = wt?.status || "";
													const wiseOpen = wStatus === "AWAITING_APPROVE" || wStatus === "SENT";
													const wiseErr = reqId ? wiseErrorByRequestId[reqId] : "";
													const submitting = wiseSubmittingKey === groupKey(r);
													const lockedLabel =
														wStatus === "AWAITING_APPROVE"
															? "Awaiting approval"
															: wStatus === "SENT"
																? "Sending…"
																: wStatus === "PAID"
																	? "Paid via Wise"
																	: "";
													return (
														<>
															{/* Pay tay bị khoá khi đang có lệnh Wise mở hoặc đã chi qua Wise — tránh chi hai lần cho một request.
															    03/11/2026: chỉ super admin thấy nút (backend cũng chặn). */}
															{isSuperAdmin ? (
															<DebtPayActionButton
																onClick={() => openPay(r)}
																style={{ width: ACTION_BTN_WIDTH, justifyContent: "center" }}
																disabled={loading || wiseOpen || wStatus === "PAID"}
																title={
																	wiseOpen
																		? "A Wise transfer is open for this request — cancel it in Wise before paying manually."
																		: wStatus === "PAID"
																			? "Already paid via Wise."
																			: undefined
																}>
																Pay
															</DebtPayActionButton>
															) : null}
															{reqId ? (
																<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
																	<div style={{ display: "flex", gap: 6, alignItems: "center" }}>
																		{lockedLabel ? (
																			<DebtApproveActionButton
																				disabled
																				title={wt ? `Wise transfer ${wt.wiseTransferId} · ${wt.wiseStatus || wStatus}` : undefined}
																				style={{
																					width: ACTION_BTN_WIDTH,
																					justifyContent: "center",
																					background: wStatus === "PAID" ? "#067647" : "#344054",
																					opacity: 1,
																					cursor: "default",
																				}}>
																				{lockedLabel}
																			</DebtApproveActionButton>
																		) : (
																			<DebtApproveActionButton
																				onClick={() => confirmPayViaWise_forRow(r)}
																				style={{ width: ACTION_BTN_WIDTH, justifyContent: "center" }}
																				disabled={loading || submitting}>
																				{submitting ? "Creating…" : "Pay via Wise"}
																			</DebtApproveActionButton>
																		)}
																		{wt && wStatus !== "PAID" ? (
																			<button
																				type="button"
																				onClick={() => refreshWiseStatus(reqId)}
																				disabled={wiseRefreshingId === reqId}
																				title="Check the transfer status on Wise now"
																				style={{ border: "1px solid #d0d5dd", background: "#fff", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 12 }}>
																				{wiseRefreshingId === reqId ? "…" : "↻"}
																			</button>
																		) : null}
																	</div>
																	{wStatus === "CANCELLED" || wStatus === "FAILED" ? (
																		<span style={{ color: "#b42318", fontSize: 11 }}>
																			{wStatus === "CANCELLED" ? "Last Wise transfer was cancelled" : `Last Wise transfer failed (${wt?.wiseStatus || "unknown"})`} — you can create a new one.
																		</span>
																	) : null}
																	{wiseErr ? (
																		<span style={{ color: "#b42318", fontSize: 11, maxWidth: 260 }}>{wiseErr}</span>
																	) : null}
																</div>
															) : null}
														</>
													);
												})()}
											</div>
										</PermissionGate>
									) : isPaid && returnedIdOf(r) ? (
										<PermissionGate permission="settlements.manage">
											{(() => {
												const reqId = Number(returnedIdOf(r));
												const info = returnedByRequestId[reqId];
												const daGui = info?.repayStatus;
												return (
													<div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
														<span
															title={`Wise transfer ${info?.wiseTransferId} was returned by the station's bank`}
															style={{ color: "#b42318", fontSize: 11, fontWeight: 600 }}>
															Returned by bank — station not paid
														</span>
														{daGui ? (
															<DebtApproveActionButton
																disabled
																style={{ width: ACTION_BTN_WIDTH, justifyContent: "center", background: "#344054", opacity: 1, cursor: "default" }}>
																{daGui === "AWAITING_APPROVE" ? "Awaiting approval" : "Sending…"}
															</DebtApproveActionButton>
														) : (
															<DebtApproveActionButton
																onClick={() => confirmResendViaWise(r, reqId)}
																style={{ width: ACTION_BTN_WIDTH, justifyContent: "center" }}
																disabled={loading || wiseSubmittingKey === groupKey(r)}>
																{wiseSubmittingKey === groupKey(r) ? "Creating…" : "Pay via Wise again"}
															</DebtApproveActionButton>
														)}
													</div>
												);
											})()}
										</PermissionGate>
									) : (
										<span style={{ color: "#9ca3af", fontSize: "12px" }}>—</span>
									)}
									</DebtTableCell>
									<DebtTableCell>
										<DebtDetailsLink
											to="#"
											onClick={(e) => {
												e.preventDefault();
												openPaymentDetailsPopup(r);
											}}>
											Details
										</DebtDetailsLink>
									</DebtTableCell>
								</DebtTableRow>
							);
						})
					)}
				</DebtTable>

				<PaginationWrapperTop
					style={{
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
						justifyContent: "space-between",
						gap: "16px",
						width: "100%",
					}}>
					<div
						style={{
							display: "flex",
							flexWrap: "wrap",
							alignItems: "center",
							gap: "10px",
							fontSize: "13px",
							color: "#475569",
						}}>
						<span style={{ fontWeight: 600 }}>{lang === "vi" ? "Số dòng/trang" : "Rows per page"}</span>
						<input
							type="number"
							min={PAGE_SIZE_MIN}
							max={PAGE_SIZE_MAX}
							value={pageSizeInput}
							onChange={(e) => setPageSizeInput(e.target.value)}
							onBlur={commitPageSizeFromInput}
							onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) => {
								if (e.key === "Enter") {
									e.currentTarget.blur();
								}
							}}
							style={{
								width: "72px",
								height: "38px",
								boxSizing: "border-box",
								padding: "0 10px",
								borderRadius: "8px",
								border: "1px solid #e5e7eb",
								fontSize: "14px",
								background: "#f9fafb",
							}}
							aria-label={lang === "vi" ? "Số dòng mỗi trang" : "Rows per page"}
						/>
						<span style={{ color: "#94a3b8", fontSize: "12px" }}>(1–100)</span>
						<div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
							{PRESET_PAGE_SIZES.map((n) => (
								<button
									key={n}
									type="button"
									onClick={() => applyPresetPageSize(n)}
									style={{
										padding: "6px 12px",
										fontSize: "13px",
										fontWeight: 600,
										borderRadius: "8px",
										border: `1px solid ${pageSize === n ? "#005E9E" : "#e5e7eb"}`,
										background: pageSize === n ? "#e0f2fe" : "#fff",
										color: pageSize === n ? "#005E9E" : "#64748b",
										cursor: "pointer",
									}}>
									{n}
								</button>
							))}
						</div>
					</div>
					<div style={{ flex: "1 1 auto", minWidth: "240px", display: "flex", justifyContent: "flex-end" }}>
						<NumberPaginationBox
							page={page || 1}
							count={total}
							per={pageSize}
							onChange={(val) => doChangePage?.(val)}
						/>
					</div>
				</PaginationWrapperTop>
			</DebtListSection>

			<PaymentHistoryModal
				isOpen={payoutDetailsRow != null}
				onClose={closePayoutDetailsModal}
				stationName={cycleDetailPayload?.stationName ?? payoutDetailsRow?.stationName ?? ""}
				cycleLabel={cycleDetailPayload?.cycleLabel ?? payoutDetailsRow?.cycleLabel ?? null}
				cyclePeriod={cycleDetailPayload?.period ?? payoutDetailsRow?.period ?? null}
				vnYear={cycleDetailPayload?.vnYear ?? payoutDetailsRow?.vnYear ?? null}
				vnMonth={cycleDetailPayload?.vnMonth ?? payoutDetailsRow?.vnMonth ?? null}
				requests={payoutModalRequests}
				loading={cycleDetailLoading}
				error={cycleDetailError}
				partnerBankDetail={paymentDetailsPartnerBank}
				cycleDetails={cycleDetailPayload}
				listRowCurrencyCode={payoutDetailsRow?.currencyCode ?? payoutDetailsRow?.station?.currencyCode ?? null}
			/>

			<PaymentConfirmModal
				isOpen={payGroup != null}
				onClose={closePay}
				onConfirm={confirmPay}
				stationName={payGroup?.stationName || ""}
				subtitle={
					payGroup
						? `${cycleNameFromPeriod(payGroup.period)} · ${monthLabelFromVn(
								payGroup.vnYear,
								payGroup.vnMonth
						  )} · paying ${payGroup.pendingRequestCount} pending request(s)`
						: null
				}
				amountHintText={
					payGroup
						? formatPartnerPayoutMoney(
								payGroup.pendingAmount,
								resolveGroupCurrencyCode(payGroup, currencyByRequestId)
						  )
						: ""
				}
				amountValue={payAmountInput}
				onAmountChange={setPayAmountInput}
				isSubmitting={paySubmitting}
				noteValue={payNote}
				onNoteChange={setPayNote}
				noteRequiredMin={MANUAL_PAY_REASON_MIN}
				partnerCity={payCity}
				partnerCountry={payCountry}
				ownerPhone={payPartnerDetail?.user?.phone || payPartnerDetail?.phone || null}
				bankName={payPartnerDetail?.bankName || null}
				bankAccountNumber={payPartnerDetail?.bankAccountNumber || null}
				bankAccountHolder={payPartnerDetail?.bankAccountHolder || null}
				confirmButtonLabel="Confirm bulk pay"
			/>

			<CycleBookingsBreakdownModal
				isOpen={bookingBreakdownRow != null}
				onClose={closeBookingBreakdown}
				loading={bookingBreakdownLoading}
				error={bookingBreakdownError}
				payload={bookingBreakdownPayload}
			/>

			<RejectReasonModal
				isOpen={rejectGroup != null}
				onClose={closeReject}
				onConfirm={confirmReject}
				stationName={rejectGroup?.station?.name || ""}
				subtitle={
					rejectGroup
						? `${cycleNameFromPeriod(rejectGroup.period)} · ${monthLabelFromVn(
								rejectGroup.vnYear,
								rejectGroup.vnMonth
						  )} · ${rejectGroup.requestIds?.length ?? 0} request(s)`
						: null
				}
				isSubmitting={rejectSubmitting}
			/>
		</>
	);
}
